import React, { useEffect, useState } from 'react';
import PageApp from '../../../Page';
import {HomeOutlined, MailOutlined, SaveOutlined, SendOutlined, SyncOutlined, InfoCircleOutlined, LoginOutlined, DisconnectOutlined, CheckCircleTwoTone, CloseCircleTwoTone,} from '@ant-design/icons';
import {Row, Col, Card, Form, Input, InputNumber, Select, Switch, Button, Divider, Space, Tooltip, message, Typography, Tag,} from 'antd';
import {getEmailConfig, saveEmailConfig, testEmailConfig,} from '../../../../apis/EmailConfigApi';

const { Title, Text } = Typography;
const { Option } = Select;

const routes = [
  { href: '', title: (<><HomeOutlined /><span>Inicio</span></>) },
  { href: '', title: (<><MailOutlined /><span>Configuración de Correos</span></>) },
];

const defaultValues = {
  provider: 'smtp',
  fromName: '',
  fromEmail: '',
  replyTo: '',
  rateLimitPerMinute: 120,
  batchSize: 100,
  retryDelaysSec: [30, 120, 300],
  // SMTP
  smtpHost: '',
  smtpPort: 587,
  smtpSecure: false,
  smtpUser: '',
  smtpPass: '',
  // Microsoft 365 / OAuth
  msTenantId: '',
  msClientId: '',
  msClientSecret: '',
  msRedirectUri: '',
  msAuthority: 'https://login.microsoftonline.com/common/oauth2/v2.0',
  msScopes: 'offline_access openid profile https://graph.microsoft.com/Mail.Send',
  msConnected: false,
  msAccessToken: null,
  msAccessTokenExp: null,
};

const providerOptions = [
  { label: 'SMTP', value: 'smtp' },
  { label: 'Microsoft 365 (OAuth)', value: 'microsoft' },
];

const mapBackendToForm = (payload = {}) => ({
  provider: payload.provider || 'smtp',
  fromName: payload.from_name || '',
  fromEmail: payload.from_email || '',
  replyTo: payload.reply_to || '',
  rateLimitPerMinute: payload.rate_limit_per_minute ?? 120,
  batchSize: payload.batch_size ?? 100,
  retryDelaysSec: payload.retry_delays_sec || [30, 120, 300],

  smtpHost: payload.smtp_host || '',
  smtpPort: payload.smtp_port ?? 587,
  smtpSecure: payload.smtp_secure ?? false,
  smtpUser: payload.smtp_user || '',
  smtpPass: '',

  msTenantId: payload.ms_tenant_id || '',
  msClientId: payload.ms_client_id || '',
  msClientSecret: '',
  msRedirectUri: payload.ms_redirect_uri || '',
  msAuthority: payload.ms_authority || 'https://login.microsoftonline.com/common/oauth2/v2.0',
  msScopes: payload.ms_scopes || 'offline_access openid profile https://graph.microsoft.com/Mail.Send',
  msConnected: payload.ms_connected ?? false,
  msAccessToken: payload.ms_access_token || null,
  msAccessTokenExp: payload.ms_access_token_exp || null,
});

const mapFormToBackend = (values) => {
  const isSmtp = values.provider === 'smtp';
  const isMs = values.provider === 'microsoft';

  const retryDelays = Array.isArray(values.retryDelaysSec)
    ? values.retryDelaysSec.map((n) => Number(n)).filter((n) => !Number.isNaN(n) && n > 0)
    : [];

  // Base común y campos específicos para el discriminador 'provider'
  const base = {
    provider: values.provider,
    from_name: values.fromName,
    from_email: values.fromEmail,
    reply_to: values.replyTo || '',
    rate_limit_per_minute: Number(values.rateLimitPerMinute) || 120,
    batch_size: Number(values.batchSize) || 100,
    retry_delays_sec: retryDelays,
  };

  if (isSmtp) {
    return {
      ...base,
      smtp_host: values.smtpHost || '',
      smtp_port: Number(values.smtpPort) || 587,
      smtp_secure: Boolean(values.smtpSecure),
      smtp_user: values.smtpUser || '',
      smtp_pass: values.smtpPass || '',
    };
  }

  if (isMs) {
    return {
      ...base,
      ms_tenant_id: values.msTenantId || '',
      ms_client_id: values.msClientId || '',
      ms_client_secret: values.msClientSecret || '',
      ms_redirect_uri: values.msRedirectUri || '',
      ms_authority: values.msAuthority || '',
      ms_scopes: values.msScopes || '',
      ms_connected: Boolean(values.msConnected),
      ms_access_token: values.msAccessToken || null,
      ms_access_token_exp: values.msAccessTokenExp || null,
    };
  }

  return base;
};

const ConfigEmailService = () => {
  const [form] = Form.useForm();
  const provider = Form.useWatch('provider', form);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  const msConnected = Form.useWatch('msConnected', form);
  const msTokenExp = Form.useWatch('msAccessTokenExp', form);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await getEmailConfig();
        if (res?.ok && res.payload) {
          const mapped = mapBackendToForm(res.payload);
          form.setFieldsValue({ ...defaultValues, ...mapped });
        } else {
          form.setFieldsValue(defaultValues);
        }
      } catch (err) {
        console.error(err);
        message.error('Error al cargar configuración de correo');
        form.setFieldsValue(defaultValues);
      } finally {
        setLoading(false);
      }
    })();
  }, [form]);

  const onSave = async () => {
    try {
      console.log('Guardando configuración de correo');
      const values = await form.validateFields();
      console.log('Valores del formulario:', values);
      const backendConfig = mapFormToBackend(values);

      const payload = {
        company_id: 0, // placeholder; backend toma company del token
        config: backendConfig,
      };

      console.log('Guardando configuración de correo (payload):', payload);
      setSaving(true);
      const res = await saveEmailConfig(payload);
      setSaving(false);

      if (res?.ok) {
        message.success('Configuración guardada');
      } else {
        message.error(res?.message || 'Error al guardar configuración');
      }
    } catch (err) {
      setSaving(false);
    }
  };

  const onTest = async () => {
    try {
      const values = await form.validateFields();
      if (!testEmail) return message.warning('Ingresa un correo de prueba');

      setTesting(true);
      const res = await testEmailConfig({
        to: testEmail,
        subject: 'Prueba configuración de nómina',
        body: 'Hola, este es un correo de prueba desde la configuración de Nómina.',
      });
      setTesting(false);

      if (res?.ok) {
        message.success('Correo de prueba enviado');
      } else {
        message.error(res?.message || 'Error al enviar el correo de prueba');
      }
    } catch (err) {
      setTesting(false);
    }
  };

  const connectMicrosoft = async () => {
    // Simulación de conexión OAuth con Microsoft
    const fakeToken = 'fake-ms-access-token';
    const exp = Date.now() + 3600 * 1000; // 1 hora desde ahora
    try {
      const values = await form.validateFields();
      // ...validaciones...
      const updatedValues = {
        ...values,
        msConnected: true,
        msAccessToken: fakeToken,
        msAccessTokenExp: exp,
      };

      const backendConfig = mapFormToBackend(updatedValues);
      const payload = { company_id: 0, config: backendConfig };
      const res = await saveEmailConfig(payload);

      if (res?.ok) {
        form.setFieldsValue(updatedValues);
        message.success({ key: 'ms', content: 'Conectado (mock)' });
      } else {
        message.error({ key: 'ms', content: 'Error al conectar (guardar config)' });
      }
    } catch (err) {
      // ...
    }
  };

  const disconnectMicrosoft = async () => {
    const values = await form.getFieldsValue(true);
    const updatedValues = {
      ...values,
      msConnected: false,
      msAccessToken: null,
      msAccessTokenExp: null,
    };

    const backendConfig = mapFormToBackend(updatedValues);
    const payload = { company_id: 0, config: backendConfig };
    const res = await saveEmailConfig(payload);

    // En proceso
  };

  return (
    <PageApp routes={routes} title="Configuración del servicio de correos">
      <Card loading={loading} title="Parámetros generales">
        <Form
          form={form}
          layout="vertical"
          initialValues={defaultValues}
          autoComplete="off"
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="provider"
                label="Proveedor"
                rules={[{ required: true }]}
              >
                <Select options={providerOptions} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="fromName"
                label="From Name"
                rules={[{ required: true }]}
              >
                <Input placeholder="Ej. Nómina ACME" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="fromEmail"
                label="From Email"
                rules={[{ required: true, type: 'email', message: 'Correo inválido' }]}
              >
                <Input placeholder="nomina@tu-dominio.com" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="replyTo"
                label="Reply-To (opcional)"
                rules={[{ type: 'email' }]}
              >
                <Input placeholder="respuestas@tu-dominio.com" />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                name="rateLimitPerMinute"
                label={
                  <Space>
                    Límite por minuto
                    <Tooltip title="Correos por minuto.">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
              >
                <InputNumber min={1} className="w-full" />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                name="batchSize"
                label={
                  <Space>
                    Tamaño de lote
                    <Tooltip title="Correos por envío.">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
              >
                <InputNumber min={1} className="w-full" />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                name="retryDelaysSec"
                label={
                  <Space>
                    Reintentos (seg)
                    <Tooltip title="Ej: 30,120,300">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                getValueFromEvent={(e) =>
                  e?.target?.value
                    ?.split(',')
                    .map((x) => x.trim())
                    .filter(Boolean)
                    .map((x) => Number(x))
                }
                getValueProps={(val) => ({
                  value: Array.isArray(val) ? val.join(',') : '',
                })}
              >
                <Input placeholder="30,120,300" />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          {/* === SMTP === */}
          {provider === 'smtp' && (
            <>
              <Title level={5}>SMTP</Title>
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item name="smtpHost" label="Host">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={4}>
                  <Form.Item name="smtpPort" label="Puerto">
                    <InputNumber min={1} className="w-full" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={4}>
                  <Form.Item
                    name="smtpSecure"
                    label="TLS/SSL"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="smtpUser" label="Usuario">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="smtpPass" label="Contraseña">
                    <Input.Password />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          {/* === Microsoft 365 (OAuth) === */}
          {provider === 'microsoft' && (
            <>
              <Title level={5}>Microsoft 365 (OAuth — Mock)</Title>
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item name="msTenantId" label="Tenant ID">
                    <Input placeholder="Tenant ID" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="msClientId" label="Client ID">
                    <Input placeholder="App Registration ID" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="msClientSecret" label="Client Secret">
                    <Input.Password />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="msRedirectUri" label="Redirect URI">
                    <Input placeholder="https://tu-app.com/auth/callback/microsoft" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="msAuthority" label="Authority">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item name="msScopes" label="Scopes">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Space align="center">
                <Tag
                  color={msConnected ? 'green' : 'red'}
                  icon={
                    msConnected ? (
                      <CheckCircleTwoTone twoToneColor="#52c41a" />
                    ) : (
                      <CloseCircleTwoTone twoToneColor="#ff4d4f" />
                    )
                  }
                >
                  {msConnected ? 'Conectado' : 'Desconectado'}
                </Tag>
                {msConnected && msTokenExp && (
                  <Text type="secondary">
                    Token expira: {new Date(msTokenExp).toLocaleString()}
                  </Text>
                )}
                {!msConnected ? (
                  <Button
                    icon={<LoginOutlined />}
                    type="primary"
                    onClick={connectMicrosoft}
                  >
                    Conectar con Microsoft (mock)
                  </Button>
                ) : (
                  <Button
                    icon={<DisconnectOutlined />}
                    danger
                    onClick={disconnectMicrosoft}
                  >
                    Desconectar
                  </Button>
                )}
              </Space>
            </>
          )}

          <Divider />

          <Space size="middle">
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              onClick={onSave}
            >
              Guardar
            </Button>

            <Input
              placeholder="correo@prueba.com"
              style={{ width: 260 }}
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
            />

            <Button
              icon={<SendOutlined />}
              loading={testing}
              onClick={onTest}
            >
              Enviar prueba
            </Button>

            <Button
              icon={<SyncOutlined />}
              onClick={async () => {
                const res = await getEmailConfig();
                if (res?.ok && res.payload) {
                  const mapped = mapBackendToForm(res.payload);
                  form.setFieldsValue({ ...defaultValues, ...mapped });
                  message.success('Configuración recargada');
                } else {
                  form.setFieldsValue(defaultValues);
                  message.warning('Sin configuración previa, usando valores por defecto');
                }
              }}
            >
              Recargar
            </Button>
          </Space>
        </Form>
      </Card>
    </PageApp>
  );
};

export default ConfigEmailService;
